import React, { useEffect, useState } from 'react';
import classes from './LoginPage.module.scss';
import { Button, Flex, Form, Input, Space, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { getSignInScheme } from '../model/scheme';
import { useIntl } from 'react-intl';
import { AppDispatch } from 'shared/config/store/store';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { CustomCardContainer, SessionEndedModal } from 'shared/ui';
import { useLoginMutation } from '../api';
import { useLazyGetUserInfoQuery, useSaveGtagMutation } from 'entities/user';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { setUserMeta } from 'shared/config/store/reducers/userSlice';
import { getErrorId, getSecondErrorId } from 'shared/libs/helpers';
import {
  isEmailVerificationRequiredInResponse,
  isPhoneVerificationRequiredInResponse,
} from '../lib/verification';
import { ERROR_IDS } from 'shared/const/api';
import { ROUTES } from 'shared/const/routes';
import { ApiError } from 'shared/model/types';
import { GTAG_ID } from 'shared/const/app';

const SignIn = () => {
  const [loading] = useState(false);
  const [showSessionEndedModal, setShowSessionEndedModal] = useState<boolean>(false);

  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { formatMessage: t } = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const password = Form.useWatch('password', form);
  const formRules = getSignInScheme(t);
  const { showError, showSuccess } = useMessage();
  const [emailErr, setEmailErr] = useState<string[]>([]);
  const [passwordErr, setPasswordErr] = useState<string[]>([]);

  const [login] = useLoginMutation();
  const [fetchUserInfo] = useLazyGetUserInfoQuery();
  const [saveGtag] = useSaveGtagMutation();

  const onFinish = ({ email, password }: { email: string; password: string }) => {
    const formData = { email, password };
    dispatch(setActivity(true));
    login(formData)
      .unwrap()
      .then((data) => {
        const { response } = data;
        
        const emailVerificationRequired = isEmailVerificationRequiredInResponse(data);
        const phoneVerificationRequired = isPhoneVerificationRequiredInResponse(data);

        const needsEmailVerification = emailVerificationRequired || (!response.verified && response.email);
        
        const needsPhoneVerification = phoneVerificationRequired || 
          (response.verified === true && !response.phoneVerified && response.phone);
        
        if (needsEmailVerification) {
          navigate({ 
            pathname: ROUTES.CONFIRM_EMAIL.path, 
            search: `email=${encodeURIComponent(response.email)}${response.phone ? `&phone=${encodeURIComponent(response.phone)}` : ''}` 
          }, { replace: true });
          dispatch(setUserMeta(response));
          gtag('get', GTAG_ID, 'client_id', (clientID) => {
            saveGtag({ clientId: clientID as string, eventName: 'web_login_successful', gtag: GTAG_ID });
            gtag('event', 'web_login_successful', {
              user_id: response.id,
              email: response.email,
              verified: response.verified,
            });
          });
          return;
        }

        if (needsPhoneVerification) {
          navigate({ 
            pathname: ROUTES.CONFIRM_PHONE.path, 
            search: `phone=${encodeURIComponent(response.phone)}&email=${encodeURIComponent(response.email)}` 
          }, { replace: true });
          // Set user meta after navigation to allow verification requests
          dispatch(setUserMeta(response));
          gtag('get', GTAG_ID, 'client_id', (clientID) => {
            saveGtag({ clientId: clientID as string, eventName: 'web_login_successful', gtag: GTAG_ID });
            gtag('event', 'web_login_successful', {
              user_id: response.id,
              email: response.email,
              verified: response.verified,
            });
          });
          return;
        }

        gtag('get', GTAG_ID, 'client_id', (clientID) => {
          saveGtag({ clientId: clientID as string, eventName: 'web_login_successful', gtag: GTAG_ID });
          gtag('event', 'web_login_successful', {
            user_id: response.id,
            email: response.email,
            verified: response.verified,
          });
        });
        dispatch(setUserMeta(response));

        showSuccess(t({ id: 'loginPage.welcome' }));
        fetchUserInfo();
        navigate(ROUTES.DASHBOARD.path, { replace: true });
      })
    .catch((error: ApiError) => {
      const errorId = getErrorId(error);
      const errorId2 = getSecondErrorId(error);

      if (errorId === 'prohibited.country.ip') {
        showError(t({ id: 'modals.prohibited.text' }));
        return;
      }

      if (errorId === ERROR_IDS.TWO_FA_NOT_PROVIDED) {
        navigate(ROUTES.TWO_FACTOR.path, { state: { email, password } });
        return;
      }

      if (
        errorId === 'screens.auth.login.errors.invalid' &&
        errorId2 !== 'errors.access.temporary.blocked'
      ) {
        showError(t({ id: 'loginPage.badCredentials' }));
        return;
      }

      if (errorId2 === 'errors.access.temporary.blocked') {
        showError(t({ id: 'errors.access.temporary.blocked' }));
        return;
      }

      showError(t({ id: 'loginPage.error' }));
    })
      .finally(() => {
        dispatch(setActivity(false));
      });
  };

  const onFieldsChange = () => {
    setEmailErr(form.getFieldError('email'));
    setPasswordErr(form.getFieldError('password'));
  };

  useEffect(() => {
    const sessionExpired = localStorage.getItem('sessionExpired');
    if (sessionExpired === 'true') {
      setShowSessionEndedModal(true);
      localStorage.removeItem('sessionExpired');
    }
  }, []);

  const handleCloseSessionEndedModal = () => {
    setShowSessionEndedModal(false);
  };

  return (
    <div className={classes.wrapper}>
      <Flex flex={1} justify="center" align="center">
        <CustomCardContainer className={classes.card}>
          <Typography.Text className={classes.companyName}>{'Weststein'}</Typography.Text>
          <Form form={form} onFinish={onFinish} onFieldsChange={onFieldsChange}>
            <Form.Item
              help={<div id="error__email">{emailErr[0]}</div>}
              rules={formRules.email}
              name="email">
              <Input placeholder={t({ id: 'inputs.email' })} />
            </Form.Item>
            <Form.Item
              help={<div id="error__password">{passwordErr[0]}</div>}
              rules={formRules.password}
              name="password"
            >
              <Input.Password
                placeholder={t({ id: 'inputs.password' })}
                visibilityToggle={{
                  onVisibleChange: () => {
                    if (!password) navigate(ROUTES.RESET_PASSWORD.path);
                  }
                }}
                iconRender={(visible) => {
                  if (password) {
                    return visible ? <EyeOutlined /> : <EyeInvisibleOutlined />;
                  } else {
                    return <Typography.Link>{t({ id: 'mobile.login.input.forgot' })}</Typography.Link>;
                  }
                }}
              />
            </Form.Item>
            <Space direction="vertical" className="w100" size={8}>
              <Button loading={loading} color="primary" variant='solid' onClick={form.submit} block>
                {t({ id: 'buttons.login' })}
              </Button>
              <Button
                disabled={loading}
                onClick={() => navigate(ROUTES.SIGN_UP.path)}
                type="text"
                block>
                {t({ id: 'buttons.signup' })}
              </Button>
            </Space>
          </Form>
        </CustomCardContainer>
      </Flex>
      <SessionEndedModal
        open={showSessionEndedModal}
        onClose={handleCloseSessionEndedModal}
      />
    </div>
  );
};

export default SignIn;
